/**
 * Goal-celebration instant replay — RENDER ONLY.
 *
 * Two independent pure layers, the same split game/audio.ts uses (its header
 * explains why: vitest's node environment has no <canvas>/AudioContext/
 * three.js, so anything under test has to be plain data + math):
 *
 *   1. THE RING BUFFER (`createReplaySystem`) — records exactly what the
 *      mount already feeds `renderer.setPlayer`/`setBall` every rendered
 *      frame (no more) into preallocated typed arrays, and plays it back at a
 *      caller-chosen speed. §15.7: zero allocation once the ring is warm —
 *      `record()`/`advance()`/`sample()` never allocate.
 *   2. THE CAMERA SCRIPT (`computeReplayCameraPose`) — a pure function of
 *      elapsed/total playback time and which goal was scored in, so its orbit
 *      is just as testable as the ring buffer with plain `expect()` calls.
 *
 * Neither layer touches three.js, the DOM, or WorldState: `record()` is fed
 * numbers the caller already has (the exact args it passes to
 * renderer.setPlayer/setBall), `sample()` hands numbers back, and the caller
 * (game/practice.ts / game/soloPractice.ts) is the one that turns around and
 * calls renderer.setPlayer/setBall/applyCamera with them through the SAME
 * seam — just historical data instead of this frame's live WorldState. The
 * sim keeps stepping underneath a playback (see the mounts' wiring); this
 * module never reads or writes it, which is what makes the "PURE RENDER"
 * contract mechanically true rather than just a comment.
 */

import { clamp01, DEG2RAD } from '@fg/shared/math';
import type { CameraPose } from '../render/camera.js';

// ---------------------------------------------------------------------------
// Ring buffer — record / advance / sample
// ---------------------------------------------------------------------------

export interface ReplayPlayerFrame {
  active: boolean;
  x: number;
  y: number;
  z: number;
  yaw: number;
  team: number;
  anim: number;
  /** The sim's `animExecId` (§11) — the action identity beside the state. */
  animExec: number;
}

export interface ReplayBallFrame {
  x: number;
  y: number;
  z: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
}

/** Scratch-object factories — callers preallocate arrays of these ONCE (the
 *  §15.7 rule) and reuse them every frame with `sample()`. */
export function createReplayPlayerFrame(): ReplayPlayerFrame {
  return { active: false, x: 0, y: 0, z: 0, yaw: 0, team: 0, anim: 0, animExec: 0 };
}
export function createReplayBallFrame(): ReplayBallFrame {
  return { x: 0, y: 0, z: 0, qx: 0, qy: 0, qz: 0, qw: 1 };
}

/**
 * Assumed max recording rate for sizing the ring (§15.7). A display refreshing
 * faster than this simply ends up with a little less than `historyS` of real
 * buffered history — never a crash, never a reallocation — which is a fine
 * trade for keeping the ring's size a plain constant.
 */
const ASSUMED_MAX_FPS = 150;

export interface ReplaySystem {
  /** True while a playback is in progress — `record()` is a no-op meanwhile. */
  readonly playing: boolean;
  /** Seconds of history currently held (≤ the `historyS` this system was built with). */
  readonly recordedS: number;
  /**
   * Append one rendered frame's worth of render-seam data. No-op while
   * playing — recording is frozen for the whole playback, per the module's
   * contract (see the header). Zero-allocation once the ring has wrapped
   * once; `players`/`ball` may be freely mutated by the caller right after
   * this call returns (nothing here retains the reference).
   */
  record(dtS: number, players: readonly ReplayPlayerFrame[], ball: ReplayBallFrame): void;
  /**
   * Start playing back the most recent `windowS` seconds of buffered history
   * (clamped to whatever is actually recorded) at `speed`× real time. Returns
   * the WALL-CLOCK duration (seconds) the caller should expect playback to
   * run for, or `0` if nothing is buffered yet — playback does not start and
   * `playing` stays false (e.g. a goal in the match's very first instant).
   * Freezes recording immediately on success.
   */
  beginPlayback(windowS: number, speed: number): number;
  /**
   * Advance playback by `dtS` real seconds. Returns `true` while playback
   * should continue; `false` the moment it reaches the end of the buffered
   * window — the buffer-end handback the caller should treat exactly like an
   * explicit skip. No-op (also returning `false`) when not playing.
   */
  advance(dtS: number): boolean;
  /**
   * Write the CURRENT playback frame into the caller's own scratch arrays —
   * zero allocation. `playersOut` must have at least the `playerCount` this
   * system was created with; entries beyond that are ignored.
   */
  sample(playersOut: readonly ReplayPlayerFrame[], ballOut: ReplayBallFrame): void;
  /** End playback immediately (skip, or an external handback signal such as
   *  the sim's phase leaving Goal) and resume accepting `record()` calls. */
  endPlayback(): void;
}

export function createReplaySystem(playerCount: number, historyS = 7): ReplaySystem {
  const capacity = Math.max(2, Math.ceil(historyS * ASSUMED_MAX_FPS));

  // ── Preallocated ring storage (§15.7) — Structure-of-Arrays, sized once. ──
  const dt = new Float32Array(capacity);
  const active = new Uint8Array(capacity * playerCount);
  const px = new Float32Array(capacity * playerCount);
  const py = new Float32Array(capacity * playerCount);
  const pz = new Float32Array(capacity * playerCount);
  const pyaw = new Float32Array(capacity * playerCount);
  const pteam = new Uint8Array(capacity * playerCount);
  const panim = new Uint8Array(capacity * playerCount);
  const panimExec = new Uint8Array(capacity * playerCount);
  const bx = new Float32Array(capacity);
  const by = new Float32Array(capacity);
  const bz = new Float32Array(capacity);
  const bqx = new Float32Array(capacity);
  const bqy = new Float32Array(capacity);
  const bqz = new Float32Array(capacity);
  const bqw = new Float32Array(capacity);

  let head = -1; // ring slot most recently written; -1 = nothing recorded yet
  let count = 0; // frames currently held, ≤ capacity
  let recordedS = 0; // sum of dt[] over the frames currently held

  // ── Playback scratch — rebuilt once per beginPlayback(), never per-frame. ─
  const cum = new Float32Array(capacity); // cum[i] = seconds from the anchor to the START of frame i
  let playing = false;
  let anchorSlot = 0; // ring slot holding frame 0 of the current playback window
  let framesInWindow = 0;
  let windowDurationS = 0;
  let speed = 1;
  let playbackT = 0; // virtual (buffered-content) seconds elapsed since the anchor
  let currentFrameIdx = 0;

  function record(dtS: number, players: readonly ReplayPlayerFrame[], ball: ReplayBallFrame): void {
    if (playing) return;
    head = (head + 1) % capacity;
    if (count < capacity) {
      count++;
    } else {
      recordedS -= dt[head]!; // evicting the slot we're about to overwrite
    }
    dt[head] = dtS;
    recordedS += dtS;
    const base = head * playerCount;
    for (let i = 0; i < playerCount; i++) {
      const p = players[i];
      const on = p !== undefined && p.active;
      active[base + i] = on ? 1 : 0;
      if (on) {
        px[base + i] = p.x;
        py[base + i] = p.y;
        pz[base + i] = p.z;
        pyaw[base + i] = p.yaw;
        pteam[base + i] = p.team;
        panim[base + i] = p.anim;
        panimExec[base + i] = p.animExec;
      }
    }
    bx[head] = ball.x;
    by[head] = ball.y;
    bz[head] = ball.z;
    bqx[head] = ball.qx;
    bqy[head] = ball.qy;
    bqz[head] = ball.qz;
    bqw[head] = ball.qw;
  }

  function beginPlayback(windowS: number, spd: number): number {
    if (count === 0 || head < 0) return 0;
    // Walk backward from `head`, collecting frames until we have `windowS`
    // seconds of history or run out of buffered frames — whichever is first.
    let slot = head;
    let n = 1; // head itself is always included
    let acc = dt[head]!;
    while (n < count && acc < windowS) {
      const prevSlot = (slot - 1 + capacity) % capacity;
      acc += dt[prevSlot]!;
      slot = prevSlot;
      n++;
    }
    anchorSlot = slot;
    framesInWindow = n;
    // Forward pass building the cumulative-time-since-anchor table.
    let t = 0;
    let s = anchorSlot;
    for (let i = 0; i < framesInWindow; i++) {
      cum[i] = t;
      t += dt[s]!;
      s = (s + 1) % capacity;
    }
    windowDurationS = t; // total buffered seconds actually available in the window
    speed = spd > 0 ? spd : 1;
    playbackT = 0;
    currentFrameIdx = 0;
    playing = true;
    return windowDurationS / speed;
  }

  function advance(dtS: number): boolean {
    if (!playing) return false;
    playbackT += dtS * speed;
    if (playbackT >= windowDurationS) {
      currentFrameIdx = framesInWindow - 1;
      playing = false;
      return false;
    }
    // Binary search: largest i with cum[i] <= playbackT.
    let lo = 0;
    let hi = framesInWindow - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (cum[mid]! <= playbackT) lo = mid;
      else hi = mid - 1;
    }
    currentFrameIdx = lo;
    return true;
  }

  function sample(playersOut: readonly ReplayPlayerFrame[], ballOut: ReplayBallFrame): void {
    const slot = (anchorSlot + currentFrameIdx) % capacity;
    const base = slot * playerCount;
    for (let i = 0; i < playerCount; i++) {
      const out = playersOut[i];
      if (!out) continue;
      const on = active[base + i] === 1;
      out.active = on;
      if (on) {
        out.x = px[base + i]!;
        out.y = py[base + i]!;
        out.z = pz[base + i]!;
        out.yaw = pyaw[base + i]!;
        out.team = pteam[base + i]!;
        out.anim = panim[base + i]!;
        out.animExec = panimExec[base + i]!;
      }
    }
    ballOut.x = bx[slot]!;
    ballOut.y = by[slot]!;
    ballOut.z = bz[slot]!;
    ballOut.qx = bqx[slot]!;
    ballOut.qy = bqy[slot]!;
    ballOut.qz = bqz[slot]!;
    ballOut.qw = bqw[slot]!;
  }

  function endPlayback(): void {
    playing = false;
  }

  return {
    get playing(): boolean {
      return playing;
    },
    get recordedS(): number {
      return recordedS;
    },
    record,
    beginPlayback,
    advance,
    sample,
    endPlayback,
  };
}

// ---------------------------------------------------------------------------
// Camera script — pure function of (elapsed, total, which goal)
// ---------------------------------------------------------------------------

/**
 * Orbit radius as a FRACTION of the pitch half-length, not a fixed metre count.
 *
 * It used to be a hard 24 m, which is the bug that made a goal replay render an
 * empty void. 24 m outside the goal line is in the stand on an 11v11 pitch and
 * well outside the stadium on the 40x25 five-a-side one — the camera ended up
 * at x=-60 on a pitch whose goal line is at -20, staring at the back of nothing.
 * Anything sized in absolute metres is wrong here, because §9.2 ships six pitch
 * presets from 40x25 to 105x68.
 */
const REPLAY_CAM_RADIUS_FRAC = 0.34;
/** Clamped so the shot is neither a close-up nor a satellite photo. */
const REPLAY_CAM_RADIUS_MIN_M = 9;
const REPLAY_CAM_RADIUS_MAX_M = 22;
const REPLAY_CAM_HEIGHT_M = 6.5;
/** Total sideways sweep over the WHOLE replay — "slowly arc", not a pan. */
const REPLAY_CAM_ARC_RAD = 40 * DEG2RAD;
const REPLAY_CAM_FOV_DEG = 42;
/** Height of the look-at point — chest height on the celebrating players. */
const REPLAY_CAM_LOOK_Y = 1.4;
/**
 * Fraction of the replay spent converging back onto the LIVE camera pose.
 *
 * ui/matchIntro.ts does exactly this and says why: a scripted camera that ends
 * on a hard cut back to the rig reads as a glitch no matter how good the shot
 * was. The replay had no such blend and cut both ways.
 */
const REPLAY_BLEND_TAIL = 0.22;

function mix(a: number, b: number, k: number): number {
  return a + (b - a) * k;
}
function clampTo(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function smoothstep01(t: number): number {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return x * x * (3 - 2 * x);
}

/**
 * Fills `out` with the scripted replay camera pose at `elapsedS` seconds into
 * a playback lasting `totalS` seconds total. `goalX`/`goalZ` is the world
 * position of the goal scored in (see the GOAL rule event's `x`/`z`, or the
 * ball's own frozen position during the celebration dwell — §5.2/beginGoal-
 * Celebration nestle it at the same spot). Pure: no three.js, no DOM, no
 * WorldState — a caller feeds this straight into `renderer.applyCamera()`.
 */
export function computeReplayCameraPose(
  elapsedS: number,
  totalS: number,
  goalX: number,
  goalZ: number,
  pitchLength: number,
  pitchWidth: number,
  livePose: CameraPose | null,
  out: CameraPose,
): void {
  const p = totalS > 1e-6 ? clamp01(elapsedS / totalS) : 1;
  const e = smoothstep01(p);
  const halfL = pitchLength * 0.5;
  const halfW = pitchWidth * 0.5;

  /*
   * SNAP TO THE GOAL LINE.
   *
   * `goalX` arrives from the GOAL rule event, which stamps the ball's position
   * at the tick the line was crossed. That is near the goal but not on it, and
   * in a captured failure it was 15 m out — which, compounded by a radius in
   * absolute metres, put the camera 40 m outside the stadium. A goal is by
   * definition AT the goal line, so anchoring there is both more correct and
   * immune to whatever the ball was doing when detection fired.
   */
  const side = goalX >= 0 ? 1 : -1;
  const gx = side * halfL;
  // Never let the anchor sit outside the posts-ish region, or the arc swings
  // the camera out over the corner flag.
  const gz = clampTo(goalZ, -halfW * 0.45, halfW * 0.45);

  const radius = clampTo(halfL * REPLAY_CAM_RADIUS_FRAC, REPLAY_CAM_RADIUS_MIN_M, REPLAY_CAM_RADIUS_MAX_M);
  const angle = -REPLAY_CAM_ARC_RAD * 0.5 + REPLAY_CAM_ARC_RAD * e;

  /*
   * The camera sits INSIDE the field of play, in front of the goal, looking
   * back at the goal mouth — note the MINUS on `side`. The old version put it
   * behind the goal line looking out, which is a real broadcast angle but sits
   * in the stand, and the stand is exactly where there is nothing to render
   * from the inside. In front of the goal is guaranteed to frame the net, the
   * scorer and the players piling on, on every pitch preset.
   */
  const camX = gx - side * radius * Math.cos(angle);
  const camZ = gz + radius * Math.sin(angle);

  // Belt and braces: whatever the arithmetic, stay within the pitch envelope.
  out.x = clampTo(camX, -halfL + 1, halfL - 1);
  out.y = REPLAY_CAM_HEIGHT_M;
  out.z = clampTo(camZ, -halfW + 1, halfW - 1);
  out.lookX = gx;
  out.lookY = REPLAY_CAM_LOOK_Y;
  out.lookZ = gz;
  out.fov = REPLAY_CAM_FOV_DEG;
  out.showBallArrow = false;
  out.ballArrowYaw = 0;
  out.offBallBlend = 0;

  /*
   * Converge onto the live rig over the last stretch, so handing back to
   * gameplay is a move rather than a cut. Same technique, and the same reason,
   * as the kickoff intro's tail blend.
   */
  if (livePose && p > 1 - REPLAY_BLEND_TAIL) {
    const k = smoothstep01((p - (1 - REPLAY_BLEND_TAIL)) / REPLAY_BLEND_TAIL);
    out.x = mix(out.x, livePose.x, k);
    out.y = mix(out.y, livePose.y, k);
    out.z = mix(out.z, livePose.z, k);
    out.lookX = mix(out.lookX, livePose.lookX, k);
    out.lookY = mix(out.lookY, livePose.lookY, k);
    out.lookZ = mix(out.lookZ, livePose.lookZ, k);
    out.fov = mix(out.fov, livePose.fov, k);
  }
}
